import { useId, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import {
  AssignmentPlan,
  AssignmentSnapshot,
  PlannerEmployee,
  slotDay,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import { filterPlannerEmployees } from "@/components/appointmentRequest/assignmentPlanner/employeeFiltering";
import PlannerEmployeeList from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeList";
import PlannerEmployeeSearch from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeSearch";
import PlannerSlotWorkspace from "@/components/appointmentRequest/assignmentPlanner/plannerSlotWorkspace";
import PlannerToggle from "@/components/appointmentRequest/assignmentPlanner/plannerToggle";
import PlannerIcon from "@/components/appointmentRequest/assignmentPlanner/plannerIcon";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

interface Props {
  snapshot: AssignmentSnapshot;
  plan: AssignmentPlan;
  initialDay: string;
  onAssign: (employeeId: string, slotId: string) => Promise<boolean>;
  onClose: () => void;
}
export default function PlannerEmployeeDialog({
  snapshot,
  plan,
  initialDay,
  onAssign,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(true);
  const [departments, setDepartments] = useState<string[]>([]);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [day, setDay] = useState(initialDay);
  const employee = snapshot.employees.find((entry) => entry.id === employeeId);
  const employees = filterPlannerEmployees(
    snapshot.employees,
    search,
    departments,
  ).filter((entry) => showAll || !plan[entry.id]);
  const chooseEmployee = (entry: PlannerEmployee) => {
    const current = snapshot.slots.find((slot) => slot.id === plan[entry.id]);
    setDay(current ? slotDay(current) : initialDay);
    setEmployeeId(entry.id);
  };
  const title = employee
    ? `${t(plan[employee.id] ? "planner.reassign" : "planner.assign")} · ${employee.name}`
    : t("planner.employees");
  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { maxWidth: "1260px", height: "min(82vh, 780px)" } }}
      onClose={onClose}
      aria-labelledby={titleId}
    >
      <div className={`${styles.toolbar} ${styles.employeeDialogHeader}`}>
        {employee && (
          <GenericButton variant="outlined" onClick={() => setEmployeeId(null)}>
            {t("back")}
          </GenericButton>
        )}
        <DialogTitle
          id={titleId}
          sx={{ flex: 1, minWidth: 0, p: "0.5rem 0", fontSize: "1.1rem" }}
        >
          {title}
          {!employee && (
            <span aria-hidden="true"> ({snapshot.employees.length})</span>
          )}
        </DialogTitle>
        <IconButton aria-label={t("close")} onClick={onClose}>
          <PlannerIcon name="x" />
        </IconButton>
      </div>
      <DialogContent className={styles.employeeOverview}>
        {employee ? (
          <PlannerSlotWorkspace
            sideBySide
            className={styles.employeePickerWorkspace}
            snapshot={snapshot}
            plan={plan}
            day={day}
            onDay={setDay}
            initialScrollDay={day}
            employeeSelection={{
              id: employee.id,
              onSelect: (slotId) => {
                void onAssign(employee.id, slotId).then((updated) => {
                  if (updated) setEmployeeId(null);
                });
              },
            }}
          />
        ) : (
          <>
            <PlannerEmployeeSearch
              employees={snapshot.employees}
              search={search}
              onSearch={setSearch}
              departments={departments}
              onDepartments={setDepartments}
              autoFocus
              toggle={<PlannerToggle all={showAll} onChange={setShowAll} />}
            />
            <PlannerEmployeeList
              snapshot={snapshot}
              plan={plan}
              employees={employees}
              withActions
              onChoose={chooseEmployee}
              className={styles.pickerEmployeeList}
              emptyMessage={t("planner.no_matching_employees")}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
